import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { registerDestructor } from '@ember/destroyable';
import { getOwner } from '@ember/owner';
import type Owner from '@ember/owner';
import type IntlService from 'ember-intl/services/intl';

export interface AutoRefreshSignature {
  Args: {
    /**
     * Callback function invoked on each refresh cycle.
     * Should return a Promise that resolves when the refresh is complete.
     */
    onRefresh: () => Promise<void>;

    /**
     * Interval in milliseconds between automatic refreshes.
     * @default 30000 (30 seconds)
     */
    intervalMs?: number;

    /**
     * Whether auto-refresh is enabled.
     * When false, the interval is paused but manual refresh is still available.
     * @default true
     */
    enabled?: boolean;

    /**
     * Whether to trigger an initial refresh on mount.
     * @default true
     */
    refreshOnMount?: boolean;
  };
  Blocks: {
    /**
     * Block receives refresh state and control functions.
     */
    default: [
      {
        /** Whether a refresh is currently in progress */
        isRefreshing: boolean;
        /** Timestamp of the last successful refresh */
        lastUpdated: Date | null;
        /** Human-readable formatted timestamp (e.g., "2:30:45 PM") */
        formattedLastUpdated: string;
        /** Manually trigger a refresh */
        refresh: () => Promise<void>;
        /** Time until next auto-refresh in seconds (null if disabled) */
        secondsUntilRefresh: number | null;
      },
    ];
  };
}

/**
 * A component that encapsulates auto-refresh polling logic.
 *
 * Handles interval setup, cleanup, and state management that would otherwise
 * be repeated across every page that needs periodic data refreshes.
 *
 * @example Basic usage with default 30s interval
 * ```gts
 * <AutoRefresh @onRefresh={{this.fetchData}} as |refresh|>
 *   <Button @onClick={{refresh.refresh}} disabled={{refresh.isRefreshing}}>
 *     {{if refresh.isRefreshing "Refreshing..." "Refresh"}}
 *   </Button>
 *   <span>Last updated: {{refresh.formattedLastUpdated}}</span>
 * </AutoRefresh>
 * ```
 *
 * @example Custom interval with enable toggle
 * ```gts
 * <AutoRefresh
 *   @onRefresh={{this.fetchClusterData}}
 *   @intervalMs={{5000}}
 *   @enabled={{this.autoRefreshEnabled}}
 *   as |refresh|
 * >
 *   <span>Refreshes in {{refresh.secondsUntilRefresh}}s</span>
 * </AutoRefresh>
 * ```
 */
export default class AutoRefresh extends Component<AutoRefreshSignature> {
  @tracked isRefreshing = false;
  @tracked lastUpdated: Date | null = null;
  @tracked secondsUntilRefresh: number | null = null;

  private refreshInterval: ReturnType<typeof setInterval> | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private lastEnabledState: boolean | undefined = undefined;
  private lastIntervalMs: number | undefined = undefined;
  private hasTriggeredInitialRefresh = false;

  constructor(owner: Owner, args: AutoRefreshSignature['Args']) {
    super(owner, args);

    // Initialize state tracking to match initial state
    // This prevents updateState from creating duplicate intervals
    this.lastEnabledState = this.isEnabled;
    this.lastIntervalMs = this.intervalMs;

    // Set up intervals if enabled
    if (this.isEnabled) {
      this.setupIntervals();
    }

    registerDestructor(this, () => {
      this.clearIntervals();
    });
  }

  private get intl(): IntlService | undefined {
    // Safely get intl service - may not be available in all test contexts
    const owner = getOwner(this);
    return owner?.lookup('service:intl') as IntlService | undefined;
  }

  get isEnabled(): boolean {
    return this.args.enabled !== false;
  }

  get intervalMs(): number {
    return this.args.intervalMs ?? 30000;
  }

  get formattedLastUpdated(): string {
    if (!this.lastUpdated) {
      return this.intl?.t('common.never', { default: 'Never' }) ?? 'Never';
    }
    const locale = this.intl?.primaryLocale;
    return this.lastUpdated.toLocaleTimeString(locale);
  }

  /**
   * Yields the refresh state to the block.
   */
  get refreshState() {
    return {
      isRefreshing: this.isRefreshing,
      lastUpdated: this.lastUpdated,
      formattedLastUpdated: this.formattedLastUpdated,
      refresh: this.refresh,
      secondsUntilRefresh: this.isEnabled ? this.secondsUntilRefresh : null,
    };
  }

  /**
   * Manually trigger a refresh. Safe to call even if a refresh is in progress
   * (will be a no-op in that case).
   */
  refresh = async () => {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;
    this.resetCountdown();

    try {
      await this.args.onRefresh();
      this.lastUpdated = new Date();
    } catch (error) {
      // Log error but don't rethrow to prevent unhandled promise rejections
      // when called from intervals or setTimeout
      console.error('AutoRefresh: onRefresh callback failed', error);
    } finally {
      this.isRefreshing = false;
    }
  };

  private setupIntervals() {
    // Clear any existing intervals first to make this method idempotent
    this.clearIntervals();

    // Main refresh interval
    this.refreshInterval = setInterval(() => {
      if (!this.isRefreshing) {
        this.refresh();
      }
    }, this.intervalMs);

    // Countdown interval (updates every second)
    this.resetCountdown();
    this.countdownInterval = setInterval(() => {
      // Pause countdown while refreshing
      if (this.isRefreshing) {
        return;
      }
      if (this.secondsUntilRefresh !== null && this.secondsUntilRefresh > 0) {
        this.secondsUntilRefresh = this.secondsUntilRefresh - 1;
      }
    }, 1000);
  }

  private clearIntervals() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.secondsUntilRefresh = null;
  }

  private resetCountdown() {
    this.secondsUntilRefresh = Math.floor(this.intervalMs / 1000);
  }

  /**
   * Handle enabled state changes and initial refresh.
   * Called from the template to react to @enabled changes.
   */
  updateState = () => {
    // Handle initial refresh on mount (deferred to avoid updating during render)
    if (!this.hasTriggeredInitialRefresh && this.args.refreshOnMount !== false) {
      this.hasTriggeredInitialRefresh = true;
      // Schedule the refresh for after render completes
      setTimeout(() => this.refresh(), 0);
    }

    const currentEnabled = this.isEnabled;
    const currentIntervalMs = this.intervalMs;

    // Handle enabled state changes
    if (this.lastEnabledState !== currentEnabled) {
      this.lastEnabledState = currentEnabled;

      if (currentEnabled) {
        this.setupIntervals();
      } else {
        this.clearIntervals();
      }
    }
    // Handle interval changes while enabled
    else if (currentEnabled && this.lastIntervalMs !== currentIntervalMs) {
      this.lastIntervalMs = currentIntervalMs;
      this.setupIntervals(); // Will clear and recreate with new interval
    }

    return ''; // Return empty string for use in template
  };

  <template>
    {{! Track enabled state changes and trigger initial refresh }}
    {{this.updateState}}
    {{yield this.refreshState}}
  </template>
}
